import React from 'react';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { Button } from '@progress/kendo-react-buttons';
import { CpsIndex } from '@cs-cms/types';
import { ViewsManagementHeaderWrapper } from '../viewsManagement/ViewsManagementHeader';
import { ViewHeader } from '../viewsManagement/userViews/privateView/viewHeader/ViewHeader';
import { SystemView } from '../viewsManagement/SystemView';
import { CreateViewDialog } from '../viewsManagement/CreateViewDialog';
import { RenameViewDialog } from '../viewsManagement/RenameViewDialog';
import { GlobalFiltersEditor } from '../viewsManagement/GlobalFiltersEditor';
import { useSystemViewManagementButtonStates } from './hooks/useSystemViewManagementButtonStates';
import { useSystemViewDialogs } from './hooks/useSystemViewDialogs';
import { useSystemViewSelection } from './hooks/useSystemViewSelection';
import { useSystemViewMutations } from './hooks/useSystemViewMutations';
import { useSystemViewUpdate } from './hooks/useSystemViewUpdate';
import { useCpsDialog } from '../../common/useCpsDialog';
import {
  useCurrentUserContext,
  useGlobalizationContext,
  useTranslation,
  useGetViews,
  useGetViewsWritePermission,
  useGetCreatePermission,
  useGetIndexes,
  ViewsProvider
} from '@cs-cms/state-management';

interface SystemViewManagementContentProps {
  indexes: CpsIndex[];
  selectedIndex: CpsIndex;
  onIndexChange: (index: CpsIndex) => void;
}

const SystemViewManagementContent: React.FC<SystemViewManagementContentProps> = ({
  indexes,
  selectedIndex,
  onIndexChange
}) => {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { showDialog, DialogComponent } = useCpsDialog();
  const { data: views = [], isLoading } = useGetViews(selectedIndex.id);

  const {
    showCreateDialog,
    showRenameDialog,
    handleCreateSystemView,
    handleDialogClose,
    handleRenameView,
    handleRenameDialogClose
  } = useSystemViewDialogs();

  const { systemViews, selectedView, hasUnsavedChanges, editingViewState, setHasUnsavedChanges, handleViewChange } =
    useSystemViewSelection({
      views,
      translate
    });

  const { handleRenameViewSubmit, handleDeleteView, handleSetAsDefault, handleCreateNewSystemView } =
    useSystemViewMutations({
      systemViews,
      indexId: selectedIndex.id,
      onDialogClose: handleDialogClose,
      onRenameDialogClose: handleRenameDialogClose,
      showDialog
    });

  const { handleUpdateView } = useSystemViewUpdate({ showDialog });

  const { data: hasWritePermission, isLoading: isWritePermissionLoading } = useGetViewsWritePermission(selectedIndex.id);
  const { data: hasCreatePermission, isLoading: isCreatePermissionLoading } = useGetCreatePermission(selectedIndex.id);

  const buttonStates = useSystemViewManagementButtonStates(
    {
      views: systemViews,
      selectedViewId: selectedView
    },
    {
      viewState: editingViewState,
      hasUnsavedChanges: hasUnsavedChanges ?? false
    },
    {
      isCreateLoading: isCreatePermissionLoading,
      isWriteLoading: isWritePermissionLoading,
      hasWrite: hasWritePermission ?? false,
      hasCreate: hasCreatePermission ?? false
    },
    selectedIndex.id
  );

  const applyIndexChange = (nextIndex: CpsIndex): void => {
    setHasUnsavedChanges?.(false);
    onIndexChange(nextIndex);
  };

  const handleIndexChange = (event: DropDownListChangeEvent): void => {
    const nextIndex = indexes.find(index => index.id === event.value?.id);
    if (!nextIndex || nextIndex.id === selectedIndex.id) {
      return;
    }

    if (hasUnsavedChanges) {
      showDialog({
        title: translate('custom.settings.adminCenter.systemViewManagement.unsavedChangesTitle'),
        message: translate('custom.settings.adminCenter.systemViewManagement.unsavedChangesMessage'),
        confirmButtonLabel: translate('custom.common.discard'),
        discardButtonLabel: translate('custom.common.cancel'),
        onConfirm: confirmed => {
          if (confirmed) {
            applyIndexChange(nextIndex);
          }
        }
      });
      return;
    }

    applyIndexChange(nextIndex);
  };

  const renderSelectedView = () => {
    const currView = systemViews.find(view => view.id === selectedView);
    if (!currView) {
      return null;
    }

    return (
      <>
        <GlobalFiltersEditor indexId={selectedIndex.id} />
        <ViewsManagementHeaderWrapper>
          <ViewHeader indexId={selectedIndex.id} />
        </ViewsManagementHeaderWrapper>
        <SystemView indexId={selectedIndex.id} />
      </>
    );
  };

  return (
    <div className="systemViewsManagement">
      <div className={`viewsManagementButtons${indexes.length > 1 ? ' withIndexDropdown' : ''}`}>
        <div className="systemViewsSelectors">
          {indexes.length > 1 && (
            <DropDownList
              data={indexes}
              label={translate('custom.toolbar.index')}
              value={selectedIndex}
              onChange={handleIndexChange}
              textField="displayName"
              dataItemKey="id"
              size={'medium'}
              className="translationsIndexDropdown"
            />
          )}
          <div className="selectViewDropdown">
            <DropDownList
              data={systemViews}
              textField="name"
              dataItemKey="id"
              value={systemViews.find(view => view.id === selectedView) || systemViews[0] || null}
              onChange={handleViewChange}
              fillMode={'solid'}
            />
          </div>
        </div>
        <div className="viewActionButtons">
          <Button
            onClick={handleCreateSystemView}
            size={'small'}
            themeColor={'primary'}
            fillMode={'solid'}
            disabled={buttonStates.isCreateDisabled}
          >
            {translate('custom.settings.adminCenter.systemViewManagement.createSystem')}
          </Button>
          <Button
            onClick={handleRenameView}
            size={'small'}
            themeColor={'light'}
            fillMode={'solid'}
            disabled={buttonStates.isRenameDisabled}
          >
            {translate('custom.settings.views.buttons.renameView')}
          </Button>
          <Button
            onClick={handleDeleteView}
            size={'small'}
            themeColor={'light'}
            fillMode={'solid'}
            disabled={buttonStates.isDeleteDisabled}
          >
            {translate('custom.settings.views.buttons.deleteView')}
          </Button>
          <Button
            onClick={handleSetAsDefault}
            size={'small'}
            themeColor={'secondary'}
            fillMode={'solid'}
            disabled={buttonStates.isSetDefaultDisabled}
          >
            {translate('custom.settings.adminCenter.systemViewManagement.setAsDefault')}
          </Button>
          <span
            title={
              buttonStates.hasIncompleteData
                ? translate('custom.settings.views.incompleteData')
                : buttonStates.isGlobalFiltersLimitExceeded
                  ? translate('custom.settings.views.globalFiltersLimitExceeded')
                  : undefined
            }
          >
            <Button
              onClick={handleUpdateView}
              size={'small'}
              themeColor={'dark'}
              fillMode={'solid'}
              title={translate('custom.common.save')}
              disabled={buttonStates.isSaveDisabled}
            >
              {translate('custom.common.save')}
            </Button>
          </span>
        </div>
      </div>

      {isLoading && <div>Loading system views...</div>}

      {!isLoading && systemViews.length === 0 && (
        <div>
          <p>{translate('custom.settings.adminCenter.systemViewManagement.noViews')}</p>
          <p>{translate('custom.settings.adminCenter.systemViewManagement.createFirst')}</p>
        </div>
      )}

      {!isLoading && systemViews.length > 0 && !selectedView && (
        <div>
          <p>{translate('custom.settings.adminCenter.systemViewManagement.selectView')}</p>
        </div>
      )}

      {!isLoading && selectedView != null && renderSelectedView()}

      <CreateViewDialog
        open={showCreateDialog}
        onClose={handleDialogClose}
        onCreateView={handleCreateNewSystemView}
        titleKey="custom.settings.adminCenter.systemViewManagement.createSystem"
        descriptionKey="custom.settings.adminCenter.systemViewManagement.createDescription"
      />

      {selectedView != null && (
        <RenameViewDialog
          open={showRenameDialog}
          onClose={handleRenameDialogClose}
          onRenameView={handleRenameViewSubmit}
          currentViewId={selectedView}
          currentViewName={systemViews.find(v => v.id === selectedView)?.name || ''}
        />
      )}

      {DialogComponent}
    </div>
  );
};

export const SystemViewManagement: React.FC = () => {
  const { selectedIndex: toolbarSelectedIndex, currentUser } = useCurrentUserContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { data: indexes, isLoading: areIndexesLoading } = useGetIndexes();
  const [selectedIndex, setSelectedIndex] = React.useState<CpsIndex | null>(null);
  const isToolbarIndexPending = (currentUser?.indexes.length ?? 0) > 0 && toolbarSelectedIndex == null;

  React.useEffect(() => {
    if (selectedIndex != null || !indexes || indexes.length === 0 || isToolbarIndexPending) {
      return;
    }

    const toolbarMatch = indexes.find(index => index.id === toolbarSelectedIndex?.id);
    setSelectedIndex(toolbarMatch ?? indexes[0]);
  }, [indexes, selectedIndex, toolbarSelectedIndex, isToolbarIndexPending]);

  if (areIndexesLoading || isToolbarIndexPending || (indexes != null && indexes.length > 0 && selectedIndex == null)) {
    return <div>Loading system views...</div>;
  }

  if (selectedIndex == null) {
    return (
      <div className="systemViewsManagement">
        <p>{translate('custom.settings.adminCenter.systemViewManagement.noViews')}</p>
      </div>
    );
  }

  return (
    <ViewsProvider key={selectedIndex.id} indexId={selectedIndex.id}>
      <SystemViewManagementContent
        indexes={indexes ?? []}
        selectedIndex={selectedIndex}
        onIndexChange={setSelectedIndex}
      />
    </ViewsProvider>
  );
};
